import { createApi } from '@reduxjs/toolkit/query/react';
import { IGame } from "types/games";

import { createBaseQuery } from './createBaseQuery';

export const gamesApi = createApi({
  reducerPath: 'gamesApi',
  baseQuery: createBaseQuery(),
  tagTypes: ['Games', 'Likes'],
  endpoints: (builder) => ({
    getGames: builder.query<IGame[], void>({
      query: () => ({ url: '/games', method: 'GET' }),
      providesTags: ['Games'],
    }),

    getAllPublicGames: builder.query<IGame[], void>({
      query: () => ({ url: '/games/public', method: 'GET' }),
      providesTags: ['Games'],
    }),

    getAllPrivateGames: builder.query<IGame[], void>({
      query: () => ({ url: '/games/private', method: 'GET' }),
      providesTags: ['Games'],
    }),

    getPublicGames: builder.query<IGame[], void>({
      query: () => ({ url: '/public/games', method: 'GET' }),
      providesTags: ['Games'],
    }),

    getPrivateGames: builder.query<IGame[], { isArchived?: boolean }>({
      query: (params) => ({
        url: '/private/games',
        method: 'GET',
        params: { isArchived: params?.isArchived ?? false },
      }),
      providesTags: ['Games'],
    }),

    getGame: builder.query<IGame, string>({
      query: (id) => ({ url: `/games/${id}`, method: 'GET' }),
      providesTags: (result, error, id) => result ? [{ type: 'Games', id }] : [{ type: 'Games' }],
    }),

    createGame: builder.mutation<IGame, Omit<IGame, 'id'>>({
      query: (data) => ({ url: '/games', method: 'POST', body: data }),
      invalidatesTags: ['Games'],
    }),

    updateGame: builder.mutation<IGame, IGame>({
      query: (data) => ({ url: `/games/${data.id}`, method: 'PUT', body: data }),
      invalidatesTags: (result, error, { id }) => result ? [{ type: 'Games', id }] : [{ type: 'Games' }],
    }),

    updatePrivateGame: builder.mutation<IGame, { id: string, isArchived: boolean }>({
      query: ({ id, isArchived }) => ({
        url: `/private/game/${id}`,
        method: 'PUT',
        body: { isArchived },
      }),
      invalidatesTags: (result, error, { id }) => result ? [{ type: 'Games', id }] : [{ type: 'Games' }],
    }),

    deleteGame: builder.mutation<void, string>({
      query: (id) => ({ url: `/games/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Games'],
    }),

    getGamesLikesMap: builder.query<Record<string, number>, void>({
      query: () => ({ url: '/games-users-likes-map', method: 'GET' }),
      providesTags: ['Likes'],
    }),

    getMyLikedGamesIds: builder.query<string[], void>({
      query: () => ({ url: '/my-likes', method: 'GET' }),
      providesTags: ['Likes'],
    }),

    addLike: builder.mutation<void, string>({
      query: (gameId) => ({ url: `/games/${gameId}/add-like`, method: 'POST' }),
      invalidatesTags: ['Likes'],
    }),

    removeLike: builder.mutation<void, string>({
      query: (gameId) => ({ url: `/games/${gameId}/remove-like`, method: 'POST' }),
      invalidatesTags: ['Likes'],
    }),
    createWizardGame: builder.mutation<{ id: string }, { data: Record<string, unknown>; configJson: Record<string, unknown> }>({
      query: ({ data, configJson }) => ({
        url: '/wizard/private-games',
        method: 'POST',
        body: { data, configJson },
      }),
      invalidatesTags: ['Games'],
    }),

  }),
});

export const {
  useGetGamesQuery,
  useGetAllPublicGamesQuery,
  useGetAllPrivateGamesQuery,
  useGetPublicGamesQuery,
  useGetPrivateGamesQuery,
  useGetGameQuery,
  useCreateGameMutation,
  useUpdateGameMutation,
  useUpdatePrivateGameMutation,
  useDeleteGameMutation,
  useGetGamesLikesMapQuery,
  useGetMyLikedGamesIdsQuery,
  useAddLikeMutation,
  useRemoveLikeMutation,
  useLazyGetGamesQuery,
  useLazyGetAllPublicGamesQuery,
  useLazyGetAllPrivateGamesQuery,
  useLazyGetPublicGamesQuery,
  useLazyGetPrivateGamesQuery,
  useLazyGetGamesLikesMapQuery,
  useLazyGetMyLikedGamesIdsQuery,
  useCreateWizardGameMutation
} = gamesApi;
